/**
 * StoryFlowCanvas - SVG-based DAG canvas for visualizing story scene graphs
 *
 * Renders scenes as nodes and scene transitions as directed edges.
 * Supports pan (mouse drag) and zoom (scroll wheel).
 * No external graph library required — uses custom BFS layout.
 *
 * @author    Claude / contexagon GmbH
 */

import React from 'react';
import {
    StorySceneNode,
    NODE_WIDTH, NODE_HEIGHT,
    START_NODE_WIDTH, START_NODE_HEIGHT,
    END_NODE_WIDTH, END_NODE_HEIGHT,
} from './StorySceneNode';


// ── Layout constants ────────────────────────────────────────────────────────

const COL_GAP = 220;   // horizontal spacing between columns
const ROW_GAP = 90;    // vertical spacing between rows
const PAD_X = 40;      // canvas left padding
const PAD_Y = 40;      // canvas top padding


// ── Graph builder ────────────────────────────────────────────────────────────

/**
 * Build graph nodes and edges from a loaded story object + a map of loaded scenes.
 *
 * @param {object} story   - full story object from API
 * @param {object} scenes  - map of { [sceneId]: sceneObject }
 * @returns {{ nodes, edges }}
 */
function buildGraph(story, scenes) {
    const fields = story.fields || {};
    const nodes = [];
    const edges = [];
    const nodeIds = new Set();

    function addNode(node) {
        if (!nodeIds.has(node.id)) {
            nodeIds.add(node.id);
            nodes.push(node);
        }
    }

    // ── Entry node ──────────────────────────────────────────────────────────
    const entryId = '__start__';
    addNode({ id: entryId, type: 'start', label: 'START' });

    // ── BFS from entry sceneselections ─────────────────────────────────────
    const entrySelections = fields.entry || [];
    const exitSelections = fields.exit || [];
    const refusalSelections = fields.refusal || [];

    // Collect all entry scene IDs
    const entrySceneIds = [];
    entrySelections.forEach(sel => {
        const sceneIds = (sel.fields && sel.fields.scene) || [];
        sceneIds.forEach(sid => {
            if (!entrySceneIds.includes(sid)) entrySceneIds.push(sid);
        });
    });

    // Exit / refusal scene IDs
    const exitSceneIds = new Set();
    exitSelections.forEach(sel => {
        ((sel.fields && sel.fields.scene) || []).forEach(sid => exitSceneIds.add(sid));
    });
    const refusalSceneIds = new Set();
    refusalSelections.forEach(sel => {
        ((sel.fields && sel.fields.scene) || []).forEach(sid => refusalSceneIds.add(sid));
    });

    // BFS queue of scene IDs to process
    const visited = new Set();
    const queue = [...entrySceneIds];

    // Add edges from START → entry scenes
    const entryMulti = entrySceneIds.length > 1;
    entrySceneIds.forEach(sid => {
        const prob = getEntryProbability(entrySelections, sid);
        edges.push({
            id: `${entryId}->${sid}`,
            from: entryId,
            to: sid,
            label: (entryMulti && prob != null) ? `${prob}%` : null,
        });
    });

    while (queue.length > 0) {
        const sid = queue.shift();
        if (visited.has(sid)) continue;
        visited.add(sid);

        const scene = scenes[sid];
        const actionCount = scene
            ? ((scene.fields && Array.isArray(scene.fields.actions)) ? scene.fields.actions.length : 0)
            : undefined;
        const sceneName = scene
            ? ((scene.fields && scene.fields.name) || scene.title || sid)
            : sid;

        addNode({ id: sid, type: 'scene', label: sceneName, sceneId: sid, actionCount });

        // Find sceneaction targets in this scene's actions
        if (scene && scene.fields && Array.isArray(scene.fields.actions)) {
            const targets = [];
            scene.fields.actions.forEach(action => {
                if (action.type === 'sceneaction') {
                    const targetIds = (action.fields && action.fields.scene) || [];
                    targetIds.forEach(tid => {
                        if (!targets.includes(tid)) targets.push(tid);
                    });
                }
            });

            const multi = targets.length > 1;
            targets.forEach((tid, i) => {
                edges.push({
                    id: `${sid}->${tid}-${i}`,
                    from: sid,
                    to: tid,
                    label: multi ? null : null, // could add condition labels later
                });
                if (!visited.has(tid) && !queue.includes(tid)) {
                    queue.push(tid);
                }
            });
        }
    }

    // ── Exit nodes ──────────────────────────────────────────────────────────
    let exitCounter = 0;
    exitSelections.forEach(sel => {
        const selScenes = (sel.fields && sel.fields.scene) || [];
        selScenes.forEach(sid => {
            const exitId = `__exit__${exitCounter++}`;
            addNode({ id: exitId, type: 'end', endType: 'exit', label: 'EXIT' });
            edges.push({ id: `${sid}->${exitId}`, from: sid, to: exitId, label: null });
        });
    });

    let refusalCounter = 0;
    refusalSelections.forEach(sel => {
        const selScenes = (sel.fields && sel.fields.scene) || [];
        selScenes.forEach(sid => {
            const refId = `__refusal__${refusalCounter++}`;
            addNode({ id: refId, type: 'end', endType: 'refusal', label: 'REFUSAL' });
            edges.push({ id: `${sid}->${refId}`, from: sid, to: refId, label: null });
        });
    });

    return { nodes, edges };
}

function getEntryProbability(entrySelections, sceneId) {
    for (const sel of entrySelections) {
        if (sel.fields && sel.fields.scene && sel.fields.scene.includes(sceneId)) {
            return sel.fields.probability != null ? sel.fields.probability : null;
        }
    }
    return null;
}


// ── BFS layout ───────────────────────────────────────────────────────────────

function layoutNodes(nodes, edges) {
    if (nodes.length === 0) return {};

    // Build adjacency list
    const adj = {};
    nodes.forEach(n => { adj[n.id] = []; });
    edges.forEach(e => {
        if (adj[e.from]) adj[e.from].push(e.to);
    });

    // BFS from __start__ (or first node)
    const startId = nodes.find(n => n.type === 'start')
        ? nodes.find(n => n.type === 'start').id
        : nodes[0].id;

    const colMap = {};  // nodeId → column index
    const rowCountPerCol = {};
    const rowMap = {};  // nodeId → row index

    const visited = new Set();
    const queue = [startId];
    colMap[startId] = 0;

    while (queue.length > 0) {
        const id = queue.shift();
        if (visited.has(id)) continue;
        visited.add(id);

        const col = colMap[id] || 0;
        rowCountPerCol[col] = (rowCountPerCol[col] || 0);
        rowMap[id] = rowCountPerCol[col];
        rowCountPerCol[col]++;

        (adj[id] || []).forEach(nextId => {
            if (!visited.has(nextId) && !queue.includes(nextId)) {
                // Assign next column if not already assigned to a later column
                const nextCol = col + 1;
                if (colMap[nextId] === undefined || colMap[nextId] < nextCol) {
                    colMap[nextId] = nextCol;
                }
                queue.push(nextId);
            }
        });
    }

    // Any unvisited nodes (unreachable) — place at the end
    nodes.forEach(n => {
        if (!visited.has(n.id)) {
            const maxCol = Math.max(0, ...Object.values(colMap));
            colMap[n.id] = maxCol + 1;
            rowMap[n.id] = rowCountPerCol[colMap[n.id]] || 0;
            rowCountPerCol[colMap[n.id]] = (rowCountPerCol[colMap[n.id]] || 0) + 1;
        }
    });

    // Convert col/row to x/y
    const positions = {};
    nodes.forEach(n => {
        positions[n.id] = {
            x: PAD_X + (colMap[n.id] || 0) * COL_GAP,
            y: PAD_Y + (rowMap[n.id] || 0) * ROW_GAP,
        };
    });

    return positions;
}


// ── Node dimensions ───────────────────────────────────────────────────────────

function getNodeDims(node) {
    if (node.type === 'start') return { w: START_NODE_WIDTH, h: START_NODE_HEIGHT };
    if (node.type === 'end') return { w: END_NODE_WIDTH, h: END_NODE_HEIGHT };
    return { w: NODE_WIDTH, h: NODE_HEIGHT };
}


// ── Edge path ─────────────────────────────────────────────────────────────────

function edgePath(fromPos, fromDims, toPos, toDims) {
    // Start: right-center of source node
    const x1 = fromPos.x + fromDims.w;
    const y1 = fromPos.y + fromDims.h / 2;
    // End: left-center of target node
    const x2 = toPos.x;
    const y2 = toPos.y + toDims.h / 2;

    // Cubic bezier: control points at midpoint x
    const cx = (x1 + x2) / 2;
    return `M ${x1},${y1} C ${cx},${y1} ${cx},${y2} ${x2},${y2}`;
}


// ── StoryFlowCanvas ─────────────────────────────────────────────────────────

class StoryFlowCanvas extends React.Component {
    state = {
        panX: 0,
        panY: 0,
        zoom: 1,
        dragging: false,
        dragStartX: 0,
        dragStartY: 0,
        dragStartPanX: 0,
        dragStartPanY: 0,
    };

    onMouseDown = (e) => {
        // Only start dragging on background (SVG or g element directly)
        if (e.target.closest && e.target.closest('[data-node]')) return;
        e.preventDefault();
        this.setState({
            dragging: true,
            dragStartX: e.clientX,
            dragStartY: e.clientY,
            dragStartPanX: this.state.panX,
            dragStartPanY: this.state.panY,
        });
    };

    onMouseMove = (e) => {
        if (!this.state.dragging) return;
        const dx = e.clientX - this.state.dragStartX;
        const dy = e.clientY - this.state.dragStartY;
        this.setState({
            panX: this.state.dragStartPanX + dx,
            panY: this.state.dragStartPanY + dy,
        });
    };

    onMouseUp = () => {
        this.setState({ dragging: false });
    };

    onWheel = (e) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        this.setState(prev => ({
            zoom: Math.max(0.3, Math.min(2, prev.zoom * delta)),
        }));
    };

    componentDidMount() {
        // Attach wheel listener as non-passive to allow preventDefault
        if (this._svgRef) {
            this._svgRef.addEventListener('wheel', this.onWheel, { passive: false });
        }
    }

    componentWillUnmount() {
        if (this._svgRef) {
            this._svgRef.removeEventListener('wheel', this.onWheel);
        }
    }

    render() {
        const { story, scenes, selectedSceneId, onSelectScene, loading } = this.props;
        const { panX, panY, zoom, dragging } = this.state;

        if (loading) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#bbb', fontSize: 15 }}>
                    Lädt Story-Graph…
                </div>
            );
        }

        if (!story) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#ccc', fontSize: 15, textAlign: 'center', padding: 20 }}>
                    Story auswählen, um den Ablaufgraph zu sehen
                </div>
            );
        }

        const { nodes, edges } = buildGraph(story, scenes);
        const positions = layoutNodes(nodes, edges);

        // Compute canvas size
        const allX = nodes.map(n => (positions[n.id] || { x: 0 }).x + getNodeDims(n).w);
        const allY = nodes.map(n => (positions[n.id] || { y: 0 }).y + getNodeDims(n).h);
        const canvasW = Math.max(...allX, 400) + PAD_X;
        const canvasH = Math.max(...allY, 300) + PAD_Y;

        // Build lookup for positions and dims
        const posMap = {};
        const dimsMap = {};
        nodes.forEach(n => {
            posMap[n.id] = positions[n.id] || { x: PAD_X, y: PAD_Y };
            dimsMap[n.id] = getNodeDims(n);
        });

        return (
            <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', backgroundColor: '#fafafa' }}>
                {/* Zoom controls */}
                <div style={{
                    position: 'absolute',
                    bottom: 16,
                    right: 16,
                    zIndex: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                }}>
                    {[
                        { label: '+', delta: 1.2 },
                        { label: '−', delta: 0.8 },
                    ].map(({ label, delta }) => (
                        <button
                            key={label}
                            onClick={() => this.setState(prev => ({ zoom: Math.max(0.3, Math.min(2, prev.zoom * delta)) }))}
                            style={{
                                width: 28,
                                height: 28,
                                border: '1px solid #ddd',
                                borderRadius: 5,
                                backgroundColor: 'white',
                                cursor: 'pointer',
                                fontSize: 16,
                                fontWeight: 700,
                                color: '#555',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                            }}
                        >
                            {label}
                        </button>
                    ))}
                    <button
                        onClick={() => this.setState({ panX: 0, panY: 0, zoom: 1 })}
                        style={{
                            width: 28,
                            height: 28,
                            border: '1px solid #ddd',
                            borderRadius: 5,
                            backgroundColor: 'white',
                            cursor: 'pointer',
                            fontSize: 10,
                            fontWeight: 700,
                            color: '#555',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                        }}
                        title="Zurücksetzen"
                    >
                        ⌂
                    </button>
                </div>

                <svg
                    ref={r => { this._svgRef = r; }}
                    width="100%"
                    height="100%"
                    style={{ cursor: dragging ? 'grabbing' : 'grab', display: 'block' }}
                    onMouseDown={this.onMouseDown}
                    onMouseMove={this.onMouseMove}
                    onMouseUp={this.onMouseUp}
                    onMouseLeave={this.onMouseUp}
                >
                    <defs>
                        <marker
                            id="arrow"
                            markerWidth="8"
                            markerHeight="8"
                            refX="6"
                            refY="3"
                            orient="auto"
                        >
                            <path d="M0,0 L0,6 L8,3 z" fill="#90a4ae" />
                        </marker>
                    </defs>

                    <g transform={`translate(${panX},${panY}) scale(${zoom})`}>

                        {/* Edges */}
                        {edges.map(edge => {
                            const fp = posMap[edge.from];
                            const tp = posMap[edge.to];
                            const fd = dimsMap[edge.from];
                            const td = dimsMap[edge.to];
                            if (!fp || !tp) return null;

                            const path = edgePath(fp, fd, tp, td);
                            const midX = ((fp.x + fd.w) + tp.x) / 2;
                            const midY = (fp.y + fd.h / 2 + tp.y + td.h / 2) / 2;

                            return (
                                <g key={edge.id}>
                                    <path
                                        d={path}
                                        fill="none"
                                        stroke="#b0bec5"
                                        strokeWidth={1.5}
                                        markerEnd="url(#arrow)"
                                    />
                                    {edge.label && (
                                        <text
                                            x={midX}
                                            y={midY - 4}
                                            textAnchor="middle"
                                            fontSize={10}
                                            fill="#90a4ae"
                                            fontWeight={600}
                                        >
                                            {edge.label}
                                        </text>
                                    )}
                                </g>
                            );
                        })}

                        {/* Nodes */}
                        {nodes.map(node => {
                            const pos = posMap[node.id];
                            const dims = dimsMap[node.id];
                            if (!pos) return null;
                            return (
                                <foreignObject
                                    key={node.id}
                                    x={pos.x}
                                    y={pos.y}
                                    width={dims.w}
                                    height={dims.h}
                                    data-node="true"
                                    style={{ overflow: 'visible' }}
                                >
                                    <StorySceneNode
                                        node={node}
                                        isSelected={node.sceneId === selectedSceneId}
                                        onClick={() => node.sceneId && onSelectScene && onSelectScene(node.sceneId)}
                                    />
                                </foreignObject>
                            );
                        })}

                    </g>
                </svg>
            </div>
        );
    }
}

export default StoryFlowCanvas;
